import React from "react";
import type { AdminBot } from "../../pages/AdminDashboard";

interface AdminBotsTabProps {
    bots: AdminBot[];
    botsTotal: number;
    loadingBots: boolean;
    botsPage: number;
    setBotsPage: React.Dispatch<React.SetStateAction<number>>;
    botsPageSize: number;
    setBotsPageSize: (size: number) => void;
    botsSearch: string;
    setBotsSearch: (search: string) => void;
    botsPoolType: string;
    setBotsPoolType: (poolType: string) => void;
    botsMethod: string;
    setBotsMethod: (method: string) => void;
    botsRunningOnly: boolean;
    setBotsRunningOnly: (runningOnly: boolean) => void;
    botsSortBy: "createdAt" | "instanceId" | "feesCollected" | "user";
    setBotsSortBy: (sortBy: "createdAt" | "instanceId" | "feesCollected" | "user") => void;
    botsSortDir: "asc" | "desc";
    setBotsSortDir: (sortDir: "asc" | "desc") => void;
    botsTotalPages: number;
    stopBot: (instanceId: string) => Promise<void>;
    isCompactLayout: boolean;
}

const AdminBotsTab: React.FC<AdminBotsTabProps> = ({
    bots,
    botsTotal,
    loadingBots,
    botsPage,
    setBotsPage,
    botsPageSize,
    setBotsPageSize,
    botsSearch,
    setBotsSearch,
    botsPoolType,
    setBotsPoolType,
    botsMethod,
    setBotsMethod,
    botsRunningOnly,
    setBotsRunningOnly,
    botsSortBy,
    setBotsSortBy,
    botsSortDir,
    setBotsSortDir,
    botsTotalPages,
    stopBot,
    isCompactLayout,
}) => {
    return (
        <div className="glass-card" style={{ padding: "16px", borderRadius: "16px" }}>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                <input
                    value={botsSearch}
                    onChange={(e) => setBotsSearch(e.target.value)}
                    placeholder="Search instance, pool, owner"
                    className="input-dark"
                    style={{ minWidth: "160px", flex: "1 1 220px", padding: "8px" }}
                />
                <select value={botsPoolType} onChange={(e) => setBotsPoolType(e.target.value)} className="input-dark" style={{ padding: "8px" }}>
                    <option value="all">All pool types</option>
                    <option value="pump">Pump</option>
                    <option value="raydium-amm">Raydium AMM</option>
                    <option value="raydium-cpmm">Raydium CPMM</option>
                    <option value="raydium-clmm">Raydium CLMM</option>
                    <option value="meteora-dlmm">Meteora DLMM</option>
                    <option value="meteora-damm-v2">Meteora DAMM v2</option>
                    <option value="meteora-damm-v1">Meteora DAMM v1</option>
                </select>
                <select value={botsMethod} onChange={(e) => setBotsMethod(e.target.value)} className="input-dark" style={{ padding: "8px" }}>
                    <option value="all">All methods</option>
                    <option value="volume">Volume</option>
                    <option value="inject">Inject</option>
                    <option value="extract">Extract</option>
                    <option value="defendFloor">Defend Floor</option>
                </select>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", color: "#9ca3af", fontSize: "13px" }}>
                    <input type="checkbox" checked={botsRunningOnly} onChange={(e) => setBotsRunningOnly(e.target.checked)} />
                    Running only
                </label>
                <select value={botsSortBy} onChange={(e) => setBotsSortBy(e.target.value as "createdAt" | "instanceId" | "feesCollected" | "user")} className="input-dark" style={{ padding: "8px" }}>
                    <option value="createdAt">Sort: Created</option>
                    <option value="instanceId">Sort: Instance</option>
                    <option value="feesCollected">Sort: Fees</option>
                    <option value="user">Sort: User</option>
                </select>
                <select value={botsSortDir} onChange={(e) => setBotsSortDir(e.target.value as "asc" | "desc")} className="input-dark" style={{ padding: "8px" }}>
                    <option value="desc">Desc</option>
                    <option value="asc">Asc</option>
                </select>
                <select value={String(botsPageSize)} onChange={(e) => setBotsPageSize(Number(e.target.value))} className="input-dark" style={{ padding: "8px" }}>
                    <option value="10">10 / page</option>
                    <option value="25">25 / page</option>
                    <option value="50">50 / page</option>
                    <option value="100">100 / page</option>
                </select>
            </div>

            {loadingBots ? (
                <p style={{ color: "#9ca3af" }}>Loading bots...</p>
            ) : (
                <>
                    <div style={{ overflowX: isCompactLayout ? "visible" : "auto" }}>
                        <div style={{ display: "grid", gap: "10px", minWidth: isCompactLayout ? undefined : "1050px" }}>
                            {bots.map((bot) => {
                                const runningMethods = Object.entries(bot.methods || {})
                                    .filter(([, method]) => (method as { isRunning?: boolean })?.isRunning)
                                    .map(([name]) => name)
                                    .join(", ");

                                return (
                                    <div
                                        key={bot.instanceId}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: isCompactLayout ? "1fr" : "1.3fr 1fr 1.2fr 1fr 0.7fr",
                                            gap: "12px",
                                            padding: "12px",
                                            borderRadius: "12px",
                                            background: "rgba(255,255,255,0.04)",
                                            border: "1px solid rgba(255,255,255,0.08)",
                                            alignItems: "center",
                                            fontSize: "14px",
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{bot.instanceId}</div>
                                            <div style={{ color: "#9ca3af", fontSize: "12px" }}>{bot.poolId}</div>
                                            <div style={{ color: "#6b7280", fontSize: "11px" }}>
                                                {bot.poolType === "raydium-amm"
                                                    ? "Raydium AMM"
                                                    : bot.poolType === "raydium-cpmm"
                                                        ? "Raydium CPMM"
                                                        : bot.poolType === "raydium-clmm"
                                                            ? "Raydium CLMM"
                                                            : bot.poolType === "meteora-dlmm"
                                                                ? "Meteora DLMM"
                                                                : bot.poolType === "meteora-damm-v2"
                                                                    ? "Meteora DAMM v2"
                                                                    : bot.poolType === "meteora-damm-v1"
                                                                        ? "Meteora DAMM v1"
                                                                        : "Pump"}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{bot.userId?.username || "Unknown"}</div>
                                            <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                                                {bot.userId?.tier === "pro" ? "Pro" : "Free"}
                                            </div>
                                        </div>
                                        <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                                            Running: {runningMethods || "None"}
                                        </div>
                                        <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                                            Vol {bot.runningTotals?.volume?.total || "0"} | Inj {bot.runningTotals?.inject?.total || "0"} | Ext {bot.runningTotals?.extract?.total || "0"}
                                        </div>
                                        <button
                                            onClick={() => stopBot(bot.instanceId)}
                                            style={{
                                                padding: "6px 10px",
                                                borderRadius: "8px",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                background: "rgba(239, 68, 68, 0.2)",
                                                color: "#fecaca",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Stop Bot
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                            {botsTotal} bots total
                        </span>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <button
                                onClick={() => setBotsPage((p) => Math.max(1, p - 1))}
                                disabled={botsPage <= 1}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    background: "rgba(255,255,255,0.05)",
                                    color: botsPage <= 1 ? "#6b7280" : "white",
                                    cursor: botsPage <= 1 ? "not-allowed" : "pointer",
                                }}
                            >
                                Prev
                            </button>
                            <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                                Page {botsPage} / {botsTotalPages}
                            </span>
                            <button
                                onClick={() => setBotsPage((p) => Math.min(botsTotalPages, p + 1))}
                                disabled={botsPage >= botsTotalPages}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    background: "rgba(255,255,255,0.05)",
                                    color: botsPage >= botsTotalPages ? "#6b7280" : "white",
                                    cursor: botsPage >= botsTotalPages ? "not-allowed" : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminBotsTab;
